import {FieldNamesMarkedBoolean,DefaultValues, FieldValues, useForm} from "react-hook-form";
import React, {FormEventHandler, useState} from "react";
import handleAxiosError from "@/custolink-logic/api/handleAxiosError";
import axios, {AxiosError} from "axios";
import toast from "@/components/ui/toast";
import Notification from "../../components/ui/Notification";
import { isNewDesign } from "@/custolink-newdesign/flag";
import { isAssetFrontendNewDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";
import { notify } from "@/custolink-newdesign/ui/toast";
import { rdaNotify } from "@/custolink-views/asset_frontend/redesigns/RdaToast";

/**
 * Which design switch decides new-toast vs old-toast for a given form. Most callers are
 * admin-area pages, so "admin" (the isNewDesign() gate) is the default - the explicit "asset"
 * value is for forms reachable on customer-facing pages (asset frontend, end_user), including
 * ones shared with an admin equivalent (e.g. VerifyEmailSharedForm, ResetPasswordSharedForm),
 * where the component itself cannot tell which side it is on and the caller has to say.
 */
export type ToastDesignSwitch = "admin" | "asset";

// Shared between the old design and the new one. The old toast self-mounts
// its own wrapper on first push, same as both new ones (see ui/toast.tsx and
// RdaToast.tsx), so branching here - rather than teaching every new-design
// page to call a toaster itself - is enough to make all three toasters
// design-aware, including on pages this hook reaches outside any shell
// (end_user *Design pages gated by the asset-frontend's own flag). "asset"
// gets the asset frontend's own RdaToast, not the admin's notify - a
// customer should see the `--rda-*` toast, not the admin's `cl:` one.
function toastValidationError(toastDesignSwitch: ToastDesignSwitch) {
  if (toastDesignSwitch === "asset") {
    if (isAssetFrontendNewDesign()) {
      rdaNotify.warning("A validation error occurred.");
    } else {
      toast.push(<Notification title={"A validation error occurred."} type="warning" />);
    }
    return;
  }
  if (isNewDesign()) {
    notify.warning("A validation error occurred.");
  } else {
    toast.push(<Notification title={"A validation error occurred."} type="warning" />);
  }
}

function toastGenericError(toastDesignSwitch: ToastDesignSwitch) {
  if (toastDesignSwitch === "asset") {
    if (isAssetFrontendNewDesign()) {
      rdaNotify.error("An error occurred.");
    } else {
      toast.push(<Notification title={"An error occurred."} type="danger" />);
    }
    return;
  }
  if (isNewDesign()) {
    notify.error("An error occurred.");
  } else {
    toast.push(<Notification title={"An error occurred."} type="danger" />);
  }
}

export default function useFormServerBacked<TT extends FieldValues = FieldValues,
  ServerResultType = unknown,
  TContext = unknown,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  defaultValues: DefaultValues<TT>,
  action: (data: TT, dirtyFields:Partial<Readonly<FieldNamesMarkedBoolean<TT>>>) => Promise<ServerResultType>,
  onSubmitSuccess: (data: TT, resultData: ServerResultType | null) => void,
  // Called instead of onSubmitSuccess when the action failed, for callers that
  // reserved something during the click and now have to undo it.
  onSubmitError?: (err: unknown) => void,
  // "admin" (default) for admin-area forms; pass "asset" from customer-facing callers so the
  // validation/error toast follows the asset-frontend switch instead. See ToastDesignSwitch.
  toastDesignSwitch: ToastDesignSwitch = "admin",
) {

  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})

  // noinspection TypeScriptValidateTypes
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    getValues,
    formState: {dirtyFields, isDirty,isSubmitting}
  } = useForm<TT, TContext, TTransformedValues>({
    defaultValues: defaultValues
    }
  );

  const handleFormSubmit: () => FormEventHandler<HTMLFormElement> = () => {

    // @ts-expect-error(..)
    return handleSubmit(async function (data) {
      try {
        const r = await action(data, dirtyFields)
        // assume good
        setServerErrors({})
        onSubmitSuccess(data, r)
        return

      } catch(err){
        onSubmitError?.(err)
        if(axios.isAxiosError(err)){
          const errorHandled = handleAxiosError(err as AxiosError)
          if(errorHandled.validationMessages != null) {
            const errs : Record<string, string> = {}
            Object.keys(errorHandled.validationMessages).forEach(key => {
              errs[key as string] = errorHandled.validationMessages[key]
            })
            setServerErrors(errs)

            toastValidationError(toastDesignSwitch);

            return
          } else {
            toastGenericError(toastDesignSwitch);
          }
        } else {
          toastGenericError(toastDesignSwitch);
        }
      }
      setServerErrors({})
    })
  }

  const ret = {
    register,
    setValue,
    reset,
    control,
    isSubmitting,
    isDirty,
    dirtyFields,
    watch,
    getValues,

    handleSubmit: handleFormSubmit,

    errors: serverErrors,
    clearErrors: ()=>{
      setServerErrors({})
    }

  }

  return ret

}